import React, { useEffect, useRef, useMemo, useState } from 'react'
import { useInfiniteHits } from 'react-instantsearch-hooks-web'
import SingleProductFilteringTable from '../SingleProductFilteringTable'
import NoImage from '../../Assets/Images/Logos/no-image.png'
import { useDispatch, useSelector } from 'react-redux'
import { getPriceInventory } from '../../redux/actions/priceProductsAction'
import { emptyCart, priceListAddToCart } from '../../redux/actions/cartActions'
import { Modal } from 'react-bootstrap'
import GenericModal from 'Components/GenericModal'
import { t } from 'i18next'
import { handleAddProductToCart } from 'Utils/functions'
import { getProductDetaillAction } from 'redux/actions/productActions'
import { useQueryClient } from '@tanstack/react-query'
import NetworkErrorAlert from '../NetworkErrorAlert'

export default function InfiniteHits({
  SearchBox,
  hitComponent: HitComponent,
  client,
  setProductDetails,
  setIsOpen,
  setOpenWithInventario,
  showProducts,
  ...props
}) {
  const { hits, isLastPage, showMore, results } = useInfiniteHits()

  const sentinelRef = useRef(null)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [products, setProducts] = useState([])
  // selectors
  const cart = useSelector(state => state.cart.cart)
  const companyId = useSelector(state => state.company.CompanyId)
  const warehouseId = useSelector(state => state.company.WarehouseId)

  const [showAlertDontAddProduct, setShowAlertDontAddProduct] = useState(false)
  const [showNetworkErrorAlert, setShowNetworkErrorAlert] = useState(false)
  const [apiErrorMessage, setApiErrorMessage] = useState(null)

  const searchColumns = useMemo(
    () => [
      {
        Header: 'Código',
        accessor: 'codigo',
      },
      {
        Header: 'Nombre del producto',
        accessor: 'descripcion',
      },
      {
        Header: 'Imagen',
        accessor: 'imagen',
        hideEnabled: true,
      },
      {
        Header: 'Marca',
        accessor: 'marca',
        hideEnabled: true,
      },
      {
        Header: t('SELL_BY_PROD_T.IN_STORE_HEADER'),
        accessor: 'inStore',
        hideEnabled: true,
      },
      {
        Header: 'Descuento',
        accessor: 'descuento',
        hideEnabled: true,
      },
      {
        Header: 'Precio',
        accessor: 'precio',
      },
      {
        Header: 'Acciones',
        accessor: 'acciones',
      },
    ],
    []
  )

  const getIdProducts = data => {
    const matches = data.map(match => {
      return match?.ProductId
    })
    return matches
  }

  const getInventoryById = async (id, client) => {
    return await dispatch(getPriceInventory(id, client))
  }

  const formatProducts = async (products, productsId) => {
    const inventory = await getInventoryById(productsId, client)

    const productsFormatted = await Promise.all(
      products.map(async product => {
        const inventoryProduct = inventory?.find(
          inv => inv?.ProductId === product?.ProductId
        )

        const bundleData = await queryClient.fetchQuery({
          queryKey: [
            'productDetailsBundle',
            product?.ProductId,
            companyId,
            warehouseId,
          ],
          queryFn: async () => {
            const productDetailRes = await getProductDetaillAction({
              CompanyId: companyId,
              WarehouseId: warehouseId,
              ProductId: product?.ProductId,
              GlobalExecution: true,
            })()

            const data = productDetailRes?.data?.[0]
            if (!data) return {}

            const pimAttributes = data?.PIMAttributes
            const serviceSku = pimAttributes?.relatedServices?.[0]?.systemCode
            const installable =
              pimAttributes?.attributes?.coreDetail?.installable
            const serviceId = pimAttributes?.relatedServices?.[0]?.productId
            const relatedServices = pimAttributes?.relatedServices

            return {
              serviceId,
              serviceSku,
              installable,
              relatedServices,
            }
          },
          staleTime: 1000 * 60 * 30,
          cacheTime: 1000 * 60 * 60 * 2,
        })

        let canInstall = false
        let installationService = null

        if (bundleData?.installable && bundleData?.serviceId) {
          canInstall = true

          installationService = {
            ...bundleData,
            productSku: product?.Sku,
          }
        }

        return {
          ...product,
          codigo: product?.Sku,
          descripcion: product?.ProductName,
          imagen: product.Images ? product?.Images[0]?.URL : NoImage,
          marca: product?.Brand,
          inStore: inventoryProduct?.WarehouseQty || 0,
          descuento: inventoryProduct?.Discount || 0,
          precio: inventoryProduct?.FinalPrice || 0,
          acciones: {
            canAdd: !!(
              inventoryProduct?.FinalPrice && inventoryProduct?.IsActive
            ),
            canInstall,
            installationService,
          },
          Cost: inventoryProduct?.Cost || 0,
          AvgCost: inventoryProduct?.AvgCost || 0,
          AvgPrice: inventoryProduct?.AvgPrice || 0,
          BasePrice: inventoryProduct?.BasePrice || 0,
          FinalPrice: inventoryProduct?.FinalPrice ?? null,
          TaxCategoryValue: inventoryProduct?.TaxCategoryValue ?? 0,
          IsActive: inventoryProduct?.IsActive ?? product?.IsActive ?? false,
          Quantity: 1,
        }
      })
    )

    setProducts(productsFormatted)
  }

  const setSearchData = data => {
    const productsId = getIdProducts(data)
    if (productsId.length !== 0) {
      formatProducts(data, productsId)
    }
  }

  const addToCart = product => {
    handleAddProductToCart({
      product,
      cart,
      dispatch,
      customerId: client?.CustomerId,
      setShowAlertDontAddProduct,
      setShowNetworkErrorAlert,
      setApiErrorMessage,
    })
  }

  const checkProductInCartByClient = () => {
    if (!navigator.onLine) return
    const newCart = [...cart]
    dispatch(emptyCart())
    saveCart(newCart)
  }

  const saveCart = newCart => {
    dispatch(priceListAddToCart(newCart, client?.CustomerId))
  }

  useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.isIntersecting && !isLastPage && showMore()
        })
      })
      observer.observe(sentinelRef.current)
      return () => observer.disconnect()
    }
  }, [isLastPage])

  useEffect(() => {
    client && checkProductInCartByClient()
  }, [client])

  useEffect(() => {
    if (hits?.length !== 0 && results?.query !== '') {
      setSearchData(hits)
    }
  }, [results?.query, results?.page])

  return (
    <div>
      <div className="ais-InfiniteHits">
        <div className="ais-InfiniteHits-list">
          <SingleProductFilteringTable
            sentinelRef={sentinelRef}
            SearchBox={SearchBox}
            data={products}
            columns={searchColumns}
            hideFooter={true}
            addToCart={addToCart}
            setProductDetails={setProductDetails}
            setIsOpen={setIsOpen}
            setOpenWithInventario={setOpenWithInventario}
          />
        </div>
      </div>
      <div>
        {/* MODAL Alert dont Add producto to cart */}
        <Modal
          show={showAlertDontAddProduct}
          backdrop="static"
          className="modal-clase-price-change"
          centered>
          <GenericModal
            setIsOpen={() => setShowAlertDontAddProduct(false)}
            showCaution={true}
            hideCheck={true}
            text={t('GENERIC_MODAL.NO_INVENTORY_ALERT')}
            mostrarButtons={false}></GenericModal>
        </Modal>
      </div>
      <NetworkErrorAlert
        open={showNetworkErrorAlert}
        message={t('GENERIC_MODAL.NETWORK_ERROR_ADD_PRODUCT')}
        onClose={() => setShowNetworkErrorAlert(false)}
      />
      <NetworkErrorAlert
        open={apiErrorMessage !== null}
        message={
          t('GENERIC_MODAL.API_ERROR_ADD_PRODUCT') +
          (apiErrorMessage ? ': ' + apiErrorMessage : '.')
        }
        onClose={() => setApiErrorMessage(null)}
      />
    </div>
  )
}
